import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Card, Form, Row, Col, Table, Button } from "react-bootstrap";
import { get_bateria_virtual_data } from "../../services/bateriaVirtual";

const defaultDestino = { cupsDestino: "", porcentaje: "" };

// Fallback quan el backend no retorna opcions de periode
const DEFAULT_PERIOD_OPTIONS = [
  { value: "mensual", label: "Mensual" },
  { value: "trimestral", label: "Trimestral" },
  { value: "anual", label: "Anual" },
];

const hasDefinedPercentage = (destino) => {
  if (!destino) {
    return false;
  }
  const porcentaje = destino.porcentaje;
  if (porcentaje === undefined || porcentaje === null) {
    return false;
  }
  const trimmed = String(porcentaje).trim();
  return trimmed !== "";
};

// Elimina duplicats per cupsDestino; en cas de conflicte, guanya el que té percentatge definit
const deduplicateDestinos = (items = []) => {
  const safeList = Array.isArray(items) ? items : [];
  const map = new Map();
  const order = [];
  safeList.forEach((destino) => {
    if (!destino) {
      return;
    }
    const cupsKey =
      destino.cupsDestino !== undefined && destino.cupsDestino !== null
        ? String(destino.cupsDestino)
        : "";
    if (!cupsKey) {
      return;
    }
    const existing = map.get(cupsKey);
    if (!existing) {
      map.set(cupsKey, { ...destino });
      order.push(cupsKey);
      return;
    }
    const incomingHasPercentage = hasDefinedPercentage(destino);
    const existingHasPercentage = hasDefinedPercentage(existing);
    if (incomingHasPercentage || !existingHasPercentage) {
      map.set(cupsKey, {
        ...existing,
        ...destino,
        porcentaje: destino.porcentaje,
        cupsDestinoName: destino.cupsDestinoName || existing.cupsDestinoName,
      });
    }
  });
  return order.map((key) => map.get(key)).filter(Boolean);
};

// Garanteix sempre almenys una fila buida
const resolveDestinos = (items) => {
  const deduplicated = deduplicateDestinos(items);
  return deduplicated.length ? deduplicated : [defaultDestino];
};

// forwardRef + useImperativeHandle exposen getPayload() al component pare (VirtualBatteryScreen)
const VirtualBattery = forwardRef(
  (
    {
      sectorType = "electrico",
      cupsOrigenName = "",
      ofertaId = null,
      initialDestinos = [],
    },
    ref
  ) => {
    // La bateria virtual només aplica a ofertes elèctriques
    const shouldRenderBattery =
      Boolean(sectorType) && String(sectorType).toLowerCase() !== "gas";

    const initialDestinosRef = useRef(initialDestinos);
    const [creaBatAuto, setCreaBatAuto] = useState(true);
    const [periodo, setPeriodo] = useState("");
    const [crmBateriaId, setCrmBateriaId] = useState("");
    const [destinos, setDestinos] = useState(() =>
      resolveDestinos(
        initialDestinosRef.current.length
          ? initialDestinosRef.current
          : [defaultDestino]
      )
    );
    const [allowedCups, setAllowedCups] = useState([]);
    const [cupsOrigenValue, setCupsOrigenValue] = useState(cupsOrigenName);
    const [periodOptions, setPeriodOptions] = useState(DEFAULT_PERIOD_OPTIONS);
    const [periodOptionsLoaded, setPeriodOptionsLoaded] = useState(false);

    useEffect(() => {
      if (!shouldRenderBattery) {
        return;
      }
      setCupsOrigenValue(cupsOrigenName);
    }, [cupsOrigenName, shouldRenderBattery]);

    useEffect(() => {
      if (!shouldRenderBattery) {
        return;
      }
      initialDestinosRef.current = initialDestinos;
    }, [initialDestinos, shouldRenderBattery]);

    useEffect(() => {
      if (!shouldRenderBattery) {
        return;
      }
      setPeriodOptionsLoaded(false);
      const loadData = async () => {
        // Sense ofertaId es reseteja a l'estat buit (formulari nou)
      if (!ofertaId) {
          setAllowedCups([]);
          setPeriodo("");
          setCrmBateriaId("");
          setCreaBatAuto(true);
          setPeriodOptions(DEFAULT_PERIOD_OPTIONS);
          setDestinos(
            resolveDestinos(
              initialDestinosRef.current.length
                ? initialDestinosRef.current
                : [defaultDestino]
            )
          );
          return;
        }

        const data = await get_bateria_virtual_data({ oferta_id: ofertaId });
        setAllowedCups(data.cups_destino_options || []);
        const bateria = data.bateria_virtual;
        const periodSelection = data.periodos_options || [];
        const mappedPeriodOptions = periodSelection.length
          ? periodSelection.map(([value, label]) => ({ value, label }))
          : DEFAULT_PERIOD_OPTIONS;
        setPeriodOptions(mappedPeriodOptions);
        setPeriodOptionsLoaded(true);
        if (bateria) {
          setCreaBatAuto(Boolean(bateria.crea_bat_auto));
          setPeriodo(bateria.periodos_aplicacion || "");
          setCrmBateriaId(bateria.id ? String(bateria.id) : "");
          setDestinos(
            resolveDestinos(
              (bateria.destinos || []).map((dest) => ({
                cupsDestino: dest.cupsDestino ? String(dest.cupsDestino) : "",
                porcentaje:
                  dest.porcentaje !== undefined && dest.porcentaje !== null
                    ? String(dest.porcentaje)
                    : "",
                cupsDestinoName: dest.cupsDestinoName,
              }))
            )
          );
          setCupsOrigenValue(
            bateria.cups_origen_name ? bateria.cups_origen_name : cupsOrigenName
          );
        }
      };
      loadData();
    }, [ofertaId, shouldRenderBattery]);

    useEffect(() => {
      if (!shouldRenderBattery || !periodOptionsLoaded) {
        return;
      }
      if (!periodo && periodOptions.length) {
        setPeriodo(periodOptions[0].value);
      }
    }, [shouldRenderBattery, periodOptionsLoaded, periodOptions, periodo]);

    const parsePercentage = useCallback((value) => {
      if (value === "" || value === null || value === undefined) {
        return null;
      }
      const normalized = ("" + value).replace(",", ".");
      const parsed = parseFloat(normalized);
      return Number.isNaN(parsed) ? null : parsed;
    }, []);

    const parseCupId = useCallback((value) => {
      const parsed = Number(value);
      return Number.isNaN(parsed) ? value : parsed;
    }, []);

    const updateDestino = (index, field, value) =>
      setDestinos((prev) =>
        prev.map((destino, idx) => {
          if (idx !== index) {
            return destino;
          }
          if (field === "cupsDestino") {
            const option = allowedCups.find(
              (cup) => String(cup.cups_id) === value
            );
            return {
              ...destino,
              cupsDestino: value,
              cupsDestinoName: option?.cups_name || destino.cupsDestinoName,
            };
          }
          return { ...destino, [field]: value };
        })
      );

    const addDestino = () =>
      setDestinos((prev) => [...prev, { ...defaultDestino }]);

    const removeDestino = (index) =>
      setDestinos((prev) => prev.filter((_, idx) => idx !== index));

    // Retorna l'estructura que espera el backend per desar la bateria virtual
    const getPayload = useCallback(() => {
      const deduplicatedDestinos = resolveDestinos(destinos);
      return {
        crea_bat_auto: creaBatAuto,
        periodo,
        crm_bateria_id: crmBateriaId,
        cups_origen_name: cupsOrigenValue,
        destinos: deduplicatedDestinos
          .filter((destino) => destino?.cupsDestino)
          .map((destino) => ({
            cupsDestino: parseCupId(destino.cupsDestino),
            porcentaje: parsePercentage(destino.porcentaje),
          })),
      };
    }, [
      creaBatAuto,
      periodo,
      crmBateriaId,
      cupsOrigenValue,
      destinos,
      parseCupId,
      parsePercentage,
    ]);

    useImperativeHandle(
      ref,
      () => ({
        getPayload,
      }),
      [getPayload]
    );

    if (!shouldRenderBattery) {
      return null;
    }

    return (
      <Card className="detail-card border-0 shadow-sm rounded-4 mb-4">
        <Card.Header className="bg-white border-bottom-0 pt-4 pb-3">
          <h4 className="text-primary fw-bold mb-0">Batería Virtual</h4>
        </Card.Header>
        <Card.Body className="p-4">
          <Form>
            {/* Section: CUPS Origen y Periodo */}
            <div className="bg-light p-4 rounded-4 border border-light mb-4">
              <Row className="g-3">
                <Col md={6}>
                  <label className="text-muted small fw-bold text-uppercase mb-2">
                    CUPS de Origen
                  </label>
                  <Form.Control
                    type="text"
                    readOnly
                    value={cupsOrigenValue}
                    className="bg-white shadow-sm"
                  />
                </Col>
                <Col md={6}>
                  <label className="text-muted small fw-bold text-uppercase mb-2">
                    Periodo de Aplicación
                  </label>
                  <Form.Select
                    value={periodo}
                    onChange={(event) => setPeriodo(event.target.value)}
                    className="bg-white shadow-sm"
                  >
                    <option value="">Selecciona un periodo</option>
                    {periodOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>
            </div>

            {/* Section: Destinos y Reparto */}
            <div className="bg-light p-4 rounded-4 border border-light mb-4">
              <h5 className="fw-bold text-secondary mb-3">Destinos y Reparto</h5>
              <div className="table-responsive">
                <Table bordered hover className="mb-0 bg-white shadow-sm">
                  <thead className="table-light">
                    <tr>
                      <th className="fw-bold text-secondary">CUPS Destino</th>
                      <th className="fw-bold text-secondary" style={{ width: "150px" }}>
                        % Reparto
                      </th>
                      <th className="fw-bold text-secondary text-center" style={{ width: "120px" }}>
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {destinos.map((destino, index) => (
                      <tr key={`${destino.cupsDestino}-${index}`}>
                        <td>
                          <Form.Select
                            value={destino.cupsDestino || ""}
                            onChange={(event) =>
                              updateDestino(index, "cupsDestino", event.target.value)
                            }
                            className="border-0 shadow-none"
                          >
                            <option value="">
                              Seleccione CUPS destino
                            </option>
                            {allowedCups.map((cup) => (
                              <option key={cup.cups_id} value={String(cup.cups_id)}>
                                {cup.cups_name}
                              </option>
                            ))}
                            {/* Opció de resguard si el CUPS ja desat no està a la llista actual */}
                            {destino.cupsDestino &&
                              !allowedCups.some(
                                (cup) => String(cup.cups_id) === destino.cupsDestino
                              ) && (
                                <option value={destino.cupsDestino}>
                                  {destino.cupsDestinoName || destino.cupsDestino}
                                </option>
                              )}
                          </Form.Select>
                        </td>
                        <td>
                          <Form.Control
                            type="number"
                            min="0"
                            max="100"
                            step="0.1"
                            placeholder="%"
                            value={destino.porcentaje}
                            onChange={(event) =>
                              updateDestino(index, "porcentaje", event.target.value)
                            }
                            className="border-0 shadow-none"
                          />
                        </td>
                        <td className="text-center">
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="rounded-pill"
                            onClick={() => removeDestino(index)}
                            disabled={destinos.length === 1}
                          >
                            Eliminar
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <div className="mt-3">
                <Button
                  variant="primary"
                  size="sm"
                  className="rounded-pill fw-bold shadow-sm"
                  onClick={addDestino}
                >
                  + Añadir Destino
                </Button>
              </div>
            </div>
          </Form>
        </Card.Body>
      </Card>
    );
  }
);

export default VirtualBattery;
